/* eslint-disable @atlassian/relay/graphql-naming */
import { graphql, readInlineData } from 'react-relay';
import type { formatRuleConnection$key } from '@atlassian/jira-relay/src/__generated__/formatRuleConnection.graphql';
import type { MappedRule } from '../../types';
import { formattingColors } from '../formatting-colors';
import type { FormattingRuleOperators } from '../operators';

export const mapFormatRuleConnection = (
	queryRef: formatRuleConnection$key | null | undefined,
	ruleCallback?: (rule: MappedRule) => void,
): MappedRule[] | null => {
	if (!queryRef) return null;

	const rules = readInlineData(
		graphql`
			fragment formatRuleConnection on JiraFormattingRuleConnection @inline {
				edges {
					node {
						id
						formattingColor {
							colorKey
						}
						formattingArea
						expression {
							__typename
							... on JiraFormattingSingleValueOperand {
								fieldId
								singleValueOperator: operator
								value
							}
							... on JiraFormattingMultipleValueOperand {
								fieldId
								multipleValueOperator: operator
								values
							}
							... on JiraFormattingNoValueOperand {
								fieldId
								noValueOperator: operator
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	const mappedRules = rules.edges
		?.map((edge, index) => {
			const rule = edge?.node;
			if (rule) {
				const { formattingArea, formattingColor } = rule;
				let fieldId = null;
				let operator: FormattingRuleOperators = 'IS';
				let value;
				switch (rule.expression.__typename) {
					case 'JiraFormattingSingleValueOperand':
						operator = rule.expression.singleValueOperator;
						({ value, fieldId } = rule.expression);
						break;
					case 'JiraFormattingMultipleValueOperand':
						operator = rule.expression.multipleValueOperator;
						value = rule.expression.values;
						fieldId = rule.expression.fieldId;
						break;
					case 'JiraFormattingNoValueOperand':
						operator = rule.expression.noValueOperator;
						fieldId = rule.expression.fieldId;
						break;
					default:
						break;
				}

				const mappedRule = {
					id: rule.id,
					fieldId,
					operator,
					value,
					color: formattingColors[formattingColor.colorKey || '']?.value,
					formattingArea,
					index,
				};

				ruleCallback && ruleCallback(mappedRule);
				return mappedRule;
			}
			return null;
		})
		.filter(Boolean);

	return mappedRules ?? null;
};
